import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import { Alert, Button, Flex, Input, Spin, Typography } from 'antd';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { PreviewInfo, PreviewUpdateData } from '@shared/api';
import { useDebouncedFunc } from '@shared/hooks/use-debounced-func';
import { useNotify } from '@shared/hooks/use-notify';
import { useAppSelector } from '@shared/redux';
import { authSelectors } from '@shared/redux/ducks/auth';
import { EmptyListState } from '@shared/ui/empty-list-state';
import { FetchingErrorState } from '@shared/ui/fetching-error-state/fetching-error-state';
import { LoadingState } from '@shared/ui/loading-state/loading-state';
import { PageHeader } from '@shared/ui/page-header';
import { Paginator } from '@shared/ui/paginator';
import { ScrollContainer } from '@shared/ui/scroll-container';
import { usePreviewGeneration } from './hooks/use-preview-generation';
import { PREVIEWS_PAGE_SIZE, usePreviews } from './hooks/use-previews';
import { PreviewCard } from './ui/preview-card';
import { PreviewEditModal } from './ui/preview-edit-modal';
import { PreviewGenerateModal } from './ui/preview-generate-modal';
import styles from './preview.module.scss';

export const Preview = () => {
  const { t } = useTranslation('translation', { keyPrefix: 'preview' });
  const notify = useNotify();
  const organizationId = useAppSelector(authSelectors.selectCurrentOrganizationId);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [isDebouncing, setIsDebouncing] = useState(false);
  const [selectedPreview, setSelectedPreview] = useState<PreviewInfo | null>(null);
  const [generatedPreview, setGeneratedPreview] = useState<PreviewInfo | null>(null);
  const [isGenerateModalOpen, setIsGenerateModalOpen] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const applySearch = useDebouncedFunc((value: string) => {
    setSearch(value.trim());
    setIsDebouncing(false);
  }, 300);
  const {
    data,
    isLoading,
    isFetching,
    isError,
    refetch,
    updatePreview,
    deletePreview,
    prependPreview,
    isUpdating,
    isDeleting,
  } = usePreviews(organizationId ?? undefined, search, page);
  const handleGenerated = useCallback(
    (preview: PreviewInfo) => {
      applySearch.cancel();
      prependPreview(preview);
      setGeneratedPreview(preview);
      setSearchInput('');
      setSearch('');
      setPage(1);
      setIsDebouncing(false);
    },
    [applySearch, prependPreview],
  );
  const { generatePreview, isGenerating } = usePreviewGeneration({
    organizationId: organizationId ?? undefined,
    onGenerated: handleGenerated,
  });
  const handleGenerate = useCallback(
    async (url: string) => {
      const status = await generatePreview(url);

      if (status === 'generating') {
        setIsGenerateModalOpen(false);
        notify({ type: 'success', message: t('generate.success') });
      }

      return status;
    },
    [generatePreview, notify, t],
  );
  const visibleItems = useMemo(() => {
    if (!data) return [];
    if (!generatedPreview || page !== 1 || search) return data.items;

    return [
      generatedPreview,
      ...data.items.filter((preview) => preview.id !== generatedPreview.id),
    ].slice(0, PREVIEWS_PAGE_SIZE);
  }, [data, generatedPreview, page, search]);
  const visibleCount = data?.count ?? 0;
  const pageCount = Math.ceil(visibleCount / PREVIEWS_PAGE_SIZE);

  useEffect(() => {
    applySearch.cancel();
    setSearchInput('');
    setSearch('');
    setPage(1);
    setIsDebouncing(false);
    setSelectedPreview(null);
    setGeneratedPreview(null);
    setIsGenerateModalOpen(false);
  }, [applySearch, organizationId]);

  useEffect(() => {
    if (!data || page <= 1) return;

    const lastPage = Math.max(1, Math.ceil(data.count / PREVIEWS_PAGE_SIZE));
    if (page > lastPage) setPage(lastPage);
  }, [data, page]);

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    setPage(1);
    setIsDebouncing(true);
    applySearch(value);
  };

  const handleSave = async (previewId: number, values: PreviewUpdateData) => {
    try {
      const updated = await updatePreview(previewId, values);
      setGeneratedPreview((current) => (current?.id === previewId ? updated : current));
      setSelectedPreview(null);
      notify({ type: 'success', message: t('notifications.updated') });
    } catch {
      notify({ message: t('notifications.updateError') });
    }
  };

  const handleDelete = async (previewId: number) => {
    try {
      await deletePreview(previewId);
      setGeneratedPreview((current) => (current?.id === previewId ? null : current));
      setSelectedPreview(null);
      notify({ type: 'success', message: t('notifications.deleted') });
    } catch {
      notify({ message: t('notifications.deleteError') });
    }
  };

  const hasItems = visibleItems.length > 0;
  const isInitialLoading = !organizationId || (isLoading && !data);
  const isBackgroundError = isError && Boolean(data);

  return (
    <Flex vertical gap={20} className={styles.root}>
      <PageHeader title={t('title')} />

      <div className={styles.toolbar}>
        <Input
          allowClear
          size="large"
          value={searchInput}
          prefix={<SearchOutlined className={styles.searchIcon} />}
          suffix={isDebouncing || isFetching ? <Spin size="small" /> : null}
          placeholder={t('search.placeholder')}
          aria-label={t('search.placeholder')}
          onChange={(event) => handleSearchChange(event.target.value)}
        />
        <Button
          type="primary"
          size="large"
          icon={<PlusOutlined />}
          disabled={!organizationId}
          className={styles.generateButton}
          onClick={() => setIsGenerateModalOpen(true)}
        >
          {t('generate.button')}
        </Button>
      </div>

      <Flex vertical gap={16} className={styles.content}>
        {isBackgroundError && (
          <Alert
            type="warning"
            showIcon
            message={t('loadError')}
            action={
              <Button size="small" onClick={() => refetch()}>
                {t('retry')}
              </Button>
            }
          />
        )}

        <ScrollContainer ref={listRef} marginRight="0" style={{ scrollbarGutter: 'stable' }}>
          <div className={styles.list}>
            {isInitialLoading && <LoadingState />}

            {!isInitialLoading && isError && !data && (
              <FetchingErrorState errorMessage={t('loadError')} refetch={refetch} />
            )}

            {!isInitialLoading && data && hasItems && (
              <Flex vertical gap={12}>
                <Flex align="center" justify="space-between" gap={12} className={styles.listHeader}>
                  <Typography.Title level={5} className={styles.listTitle}>
                    {t('listTitle')}
                  </Typography.Title>
                  <Typography.Text type="secondary" className={styles.count}>
                    {t('count', { count: visibleCount })}
                  </Typography.Text>
                </Flex>

                <div className={styles.grid}>
                  {visibleItems.map((preview) => (
                    <PreviewCard preview={preview} onEdit={setSelectedPreview} key={preview.id} />
                  ))}
                </div>

                {pageCount > 1 && (
                  <Flex justify="center" className={styles.pagination}>
                    <Paginator
                      initialPage={page}
                      pageCount={pageCount}
                      onPageChange={setPage}
                      scrollContainer={listRef.current}
                    />
                  </Flex>
                )}
              </Flex>
            )}

            {!isInitialLoading && data && !hasItems && (
              <EmptyListState
                className={styles.empty}
                title={t('empty.title')}
                description={search ? t('empty.search') : t('empty.organization')}
              />
            )}
          </div>
        </ScrollContainer>
      </Flex>

      <PreviewEditModal
        preview={selectedPreview}
        isUpdating={isUpdating}
        isDeleting={isDeleting}
        onClose={() => setSelectedPreview(null)}
        onSave={handleSave}
        onDelete={handleDelete}
      />

      <PreviewGenerateModal
        open={isGenerateModalOpen}
        isGenerating={isGenerating}
        onClose={() => !isGenerating && setIsGenerateModalOpen(false)}
        onGenerate={handleGenerate}
      />
    </Flex>
  );
};
